import { useEffect, useState } from 'react';

const DISMISS_KEY = 'ss_pwa_install_dismissed_v1';

/**
 * Detects iOS Safari (where the only install path is "Add to Home Screen" via
 * the Share sheet — no beforeinstallprompt event) and surfaces a slim
 * dismissable banner with the steps. Hidden when:
 *   - Already running standalone (display-mode: standalone OR navigator.standalone)
 *   - User dismissed (localStorage flag persists across sessions)
 *   - Not iOS Safari
 *
 * Android / desktop Chrome get the native A2HS prompt automatically once the
 * manifest + SW are valid, so we don't need to render anything for them here.
 */
export function InstallPrompt(): JSX.Element | null {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Already installed?
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      // iOS-specific Safari flag
      (window.navigator as { standalone?: boolean }).standalone === true;
    if (isStandalone) return;

    // User dismissed before?
    if (window.localStorage.getItem(DISMISS_KEY) === '1') return;

    // iOS Safari sniff. We intentionally narrow to Safari (excluding CriOS /
    // FxiOS / EdgiOS) because only Safari on iOS supports A2HS.
    const ua = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !('MSStream' in window);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (!isIOS || !isSafari) return;

    setVisible(true);
  }, []);

  const handleDismiss = (): void => {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // localStorage may be unavailable in private mode — fail silently.
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Install Service Steady"
      className="fixed inset-x-3 bottom-3 z-50 rounded-xl border border-ink-200 bg-white p-3 shadow-lg dark:border-[#334155] dark:bg-[#1E293B]"
    >
      <div className="flex items-start gap-3">
        <img
          src="/icons/icon-192.png"
          alt=""
          aria-hidden="true"
          className="h-10 w-10 flex-shrink-0 rounded-lg"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink-900 dark:text-[#E2E8F0]">Install Service Steady</p>
          <p className="mt-0.5 text-xs text-ink-500 dark:text-[#94A3B8]">
            Tap <span className="font-medium">Share</span>, then{' '}
            <span className="font-medium">Add to Home Screen</span> for the full app
            experience.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss install prompt"
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-ink-500 hover:bg-ink-100 dark:text-[#94A3B8] dark:hover:bg-[#0F172A]"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
